import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { LogOut, Menu, X } from 'lucide-react';

// Importaciones de páginas
import CrearPropiedad from './pages/CrearPropiedad';
import LeadsKanban from './pages/LeadsKanban';
import VerPropiedades from './pages/VerPropiedades';
import Login from './pages/Login';
import RutaPrivada from './components/RutaPrivada';
import VerPlano from './pages/VerPlano';
import GestionUsuarios from './pages/GestionUsuarios';
import VerPropiedad from './pages/VerPropiedad';
import EditarPropiedad from './pages/EditarPropiedad';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import CrearPost from './pages/CrearPost';
import Inicio from './pages/Inicio';
import LeadDetail from './pages/LeadDetail';
import { AuthProvider } from './context/AuthContext';
import SessionExpiredModal from './components/SessionExpiredModal';

function App() {
  const [usuario, setUsuario] = useState(() => {
    const storedUser = localStorage.getItem('usuario');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Detectar scroll para cambiar estilo de navbar
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Escuchar cambios en el estado de autenticación
  useEffect(() => {
    const handleStorageChange = () => {
      const storedUser = localStorage.getItem('usuario');
      setUsuario(storedUser ? JSON.parse(storedUser) : null);
    };

    window.addEventListener('storage', handleStorageChange);
    
    // También escucha eventos personalizados del AuthContext
    const handleAuthChange = () => {
      const storedUser = localStorage.getItem('usuario');
      setUsuario(storedUser ? JSON.parse(storedUser) : null);
    };

    window.addEventListener('auth-change', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-change', handleAuthChange);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('usuario');
    setUsuario(null);
    
    // Disparar eventos para actualizar otros componentes
    window.dispatchEvent(new Event('storage'));
    window.dispatchEvent(new CustomEvent('auth-change'));
    
    // Redirigir a inicio
    window.location.href = '/';
  };

  // Cerrar menú móvil al cambiar de ruta
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const ocultarFooter = location.pathname === '/plano';

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <SessionExpiredModal />
      {/* Navbar Minimalista */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/80 backdrop-blur-md border-b border-slate-100 py-3' : 'bg-transparent py-5'
      }`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          
          {/* Logo con estilo Serif */}
          <Link to="/" className="flex items-center group">
            <span className="text-2xl font-serif tracking-tighter flex items-center italic">
              V<span className="font-sans not-italic font-light tracking-widest uppercase text-xs ml-1 border-l border-slate-900 pl-2">Estates</span>
            </span>
          </Link>

          {/* Desktop Nav - ENLACES VISIBLES PARA TODOS */}
          <div className="hidden lg:flex items-center space-x-2">
            <NavLink to="/" label="Inicio" />
            <NavLink to="/blog" label="Editorial" />
            <NavLink to="/propiedades" label="Propiedades" />
            <NavLink to="/plano" label="Mapa" />
            
            {/* Solo para usuarios autenticados */}
            {usuario && (
              <>
                <div className="h-4 w-[1px] bg-slate-200 mx-2" />
                {['asesor_interno', 'superadmin'].includes(usuario?.rol) && (
                  <NavLink to="/crear" label="Nueva Propiedad" />
                )}
                {/* {['asesor_interno', 'superadmin'].includes(usuario?.rol) && (
                  <NavLink to="/clientes" label="Clientes" />
                )} */}
                
                {usuario?.rol === 'superadmin' && (
                  <NavLink to="/usuarios" label="Equipo" />
                )}

              </>
            )}
          </div>

          {/* User Actions */}
          <div className="flex items-center space-x-6">
            {usuario ? (
              <div className="flex items-center space-x-6">
                <div className="hidden sm:flex flex-col items-end">
                  <span className="text-xs font-bold uppercase tracking-tighter">{usuario.nombre}</span>
                  <span className="text-[10px] text-slate-400 uppercase tracking-widest">{usuario.rol?.replace('_', ' ')}</span>
                </div>
                <button 
                  onClick={handleLogout}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                  title="Cerrar sesión"
                >
                  <LogOut size={20} strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="text-xs font-bold uppercase tracking-[0.2em] border-b-2 border-slate-900 pb-1 hover:text-blue-600 hover:border-blue-600 transition-all"
              >
                Login
              </Link>
            )}

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-1"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay - ENLACES VISIBLES PARA TODOS */}
      <div className={`fixed inset-0 bg-white z-40 lg:hidden transition-transform duration-500 ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full pt-32 px-10 space-y-8">
          <MobileNavLink to="/" label="Inicio" />
          <MobileNavLink to="/blog" label="Editorial" />
          <MobileNavLink to="/propiedades" label="Propiedades" />
          <MobileNavLink to="/plano" label="Mapa" />
          
          {/* Solo para usuarios autenticados en mobile */}
          {usuario && (
            <>
              {['asesor_interno', 'superadmin'].includes(usuario?.rol) && (
                <MobileNavLink to="/crear" label="Nueva Propiedad" />
              )}
              {usuario?.rol === 'superadmin' && (
                <MobileNavLink to="/usuarios" label="Equipo" />
              )}
              <button onClick={handleLogout} className="text-left text-red-500 font-bold uppercase tracking-widest text-sm pt-10">
                Cerrar Sesión —
              </button>
            </>
          )}
          
          {/* Mostrar login si no hay usuario */}
          {!usuario && (
            <MobileNavLink to="/login" label="Iniciar Sesión" />
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="min-h-screen pt-16">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Inicio />} />
          <Route path="/propiedades" element={<VerPropiedades />} />
          <Route path="/crear" element={<RutaPrivada><CrearPropiedad /></RutaPrivada>} />
          <Route path="/blog" element={<Blog />} />
<Route path="/blog/crear" element={<RutaPrivada rolesPermitidos={['superadmin', 'editor']}><CrearPost /></RutaPrivada>} />
<Route path="/clientes" element={<RutaPrivada rolesPermitidos={['superadmin', 'asesor_interno']}><LeadsKanban /></RutaPrivada>} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/propiedad/:id" element={<VerPropiedad />} />
          <Route path="/editar/:id" element={<RutaPrivada><EditarPropiedad /></RutaPrivada>} />
          <Route path="/plano" element={<VerPlano />} />
          <Route path="/usuarios" element={<RutaPrivada><GestionUsuarios /></RutaPrivada>} />
          <Route path="*" element={<Navigate to="/" />} />
          <Route path="/leads/:id" element={<LeadDetail />} />

        </Routes>
      </main>

      {/* Footer Minimal */}
      {!ocultarFooter && (
        <footer className="bg-slate-900 text-white py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-12 border-b border-white/10 pb-20">
              <div className="col-span-2">
                <span className="text-3xl font-serif italic block mb-6">VEstates</span>
                <p className="text-slate-400 max-w-sm leading-relaxed">
                  Redefiniendo el estándar de la consultoría inmobiliaria a través de un servicio curado y humano.
                </p>
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase tracking-widest mb-6 text-slate-500">Legal</h4>
                <ul className="space-y-4 text-sm">
                  <li><Link to="/" className="hover:text-blue-400 transition-colors">Privacidad</Link></li>
                  <li><Link to="/" className="hover:text-blue-400 transition-colors">Términos</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase tracking-widest mb-6 text-slate-500">Contacto</h4>
                <ul className="space-y-4 text-sm text-slate-400">
                  <li>hello@vestates.com</li>
                  <li>+1 234 567 890</li>
                </ul>
              </div>
            </div>
            <div className="mt-10 flex justify-between items-center text-[10px] uppercase tracking-[0.3em] text-slate-500">
              <p>© {new Date().getFullYear()} VEstates Global</p>
              <p>Hecho con precisión</p>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

function NavLink({ to, label }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link
      to={to}
      className={`px-4 py-2 text-[11px] font-bold uppercase tracking-[0.2em] transition-all duration-300 ${
        isActive ? 'text-blue-600' : 'text-slate-500 hover:text-slate-900'
      }`}
    >
      {label}
    </Link>
  );
}

function MobileNavLink({ to, label }) {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Link 
      to={to} 
      className={`text-4xl font-light transition-all duration-300 ${
        isActive ? 'text-blue-600 italic' : 'text-slate-900 hover:italic'
      }`}
    >
      {label}
    </Link>
  );
}

export default function AppWrapper() {
  return (
    <AuthProvider>
      <Router>
        <App />
      </Router>
    </AuthProvider>
  );
}